import { SsoIdToken, SsoProviderConfig } from './sso-models';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** Google clamps the rendered button to this range. */
const MIN_BUTTON_WIDTH = 200;
const MAX_BUTTON_WIDTH = 400;

type GisCredentialResponse = {
    credential?: string;
};

type GisIdConfiguration = {
    client_id: string;
    callback: (response: GisCredentialResponse) => void;
    nonce?: string;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    itp_support?: boolean;
};

type GisButtonConfiguration = {
    type: 'standard';
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    size?: 'large' | 'medium' | 'small';
    shape?: 'rectangular' | 'pill' | 'circle' | 'square';
    text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
    logo_alignment?: 'left' | 'center';
    width?: number;
};

type GoogleAccountsId = {
    initialize: (config: GisIdConfiguration) => void;
    renderButton: (
        parent: HTMLElement,
        options: GisButtonConfiguration
    ) => void;
    cancel: () => void;
    disableAutoSelect: () => void;
};

declare global {
    interface Window {
        google?: {
            accounts: {
                id: GoogleAccountsId;
            };
        };
    }
}

let gisPromise: Promise<GoogleAccountsId> | null = null;

/**
 * Loads Google's script on first use rather than from index.html, so it stays
 * off every page that does not show a sign-in button. Cached, because
 * `initialize` is global and the script must only be added once.
 */
function loadGis(): Promise<GoogleAccountsId> {
    if (gisPromise) {
        return gisPromise;
    }

    gisPromise = new Promise<GoogleAccountsId>((resolve, reject) => {
        const existing = window.google?.accounts.id;
        if (existing) {
            resolve(existing);
            return;
        }

        const script = document.createElement('script');
        script.src = GIS_SRC;
        script.async = true;
        script.onload = () => {
            const accountsId = window.google?.accounts.id;
            if (accountsId) {
                resolve(accountsId);
            } else {
                reject(new Error('SSO_GIS_UNAVAILABLE'));
            }
        };
        script.onerror = () => {
            // Let the next attempt retry rather than caching the failure.
            gisPromise = null;
            reject(new Error('SSO_GIS_LOAD_FAILED'));
        };

        document.head.appendChild(script);
    });

    return gisPromise;
}

/** A nonce the API can check the token's claim against. */
function createNonce(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    return Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
}

export type MountGisButtonOptions = {
    width: number;
    onIdToken: (token: SsoIdToken) => void;
    onError: (error: unknown) => void;
};

/**
 * Renders Google's own button into `parent`. Google requires its branded button
 * on the web, and it is also the only way to start this flow — there is no
 * imperative entry point that yields an ID token.
 *
 * Returns a cleanup function.
 */
export function mountGisButton(
    config: SsoProviderConfig,
    parent: HTMLElement,
    options: MountGisButtonOptions
): () => void {
    const { width, onIdToken, onError } = options;
    const nonce = createNonce();

    let cancelled = false;

    loadGis()
        .then((accountsId) => {
            if (cancelled) {
                return;
            }

            accountsId.initialize({
                client_id: config.clientId,
                nonce,
                // No One Tap and no automatic sign-in: the user asked for a
                // login page, so the button is the only entry point.
                auto_select: false,
                cancel_on_tap_outside: true,
                itp_support: true,
                callback: (response) => {
                    if (cancelled) {
                        return;
                    }

                    if (response.credential) {
                        onIdToken({ idToken: response.credential, nonce });
                    } else {
                        onError(new Error('SSO_NO_ID_TOKEN'));
                    }
                }
            });

            accountsId.renderButton(parent, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                shape: 'rectangular',
                text: 'continue_with',
                logo_alignment: 'left',
                width: Math.round(
                    Math.min(
                        Math.max(width, MIN_BUTTON_WIDTH),
                        MAX_BUTTON_WIDTH
                    )
                )
            });
        })
        .catch((error: unknown) => {
            if (!cancelled) {
                onError(error);
            }
        });

    return () => {
        cancelled = true;
        window.google?.accounts.id.cancel();
        parent.replaceChildren();
    };
}
